'use client';
import { useGSAP } from '@gsap/react';
import { memo, useState } from 'react';
import { Box, Flex, Grid, GridItem, Text, VStack } from '@chakra-ui/react';

import BlockDiagonalDivider from 'shared/BlockDiagonalDivider';
import { requirementsAnimation } from 'shared/gsap';
import BlockShadow from 'shared/BlockShadow';
import ButtonGoTo from 'shared/ButtonGoTo';
import InterText from 'shared/InterText';
import NextImage from 'shared/NextImage';
import Slider from 'shared/Slider';

import { SECTIONS_IDS } from 'constants/global';
import { textCardsData } from 'constants/requirements';
import Bg from 'assets/illustrations/requirements/bg.webp';
import Cup from 'assets/illustrations/requirements/cup.webp';

const bgImagesStyles = {
  w: 'full',
  minW: { mobile: '650px', tablet: '850px', md: '900px', mg: 'unset' },
  bottom: '0',
  left: {
    mobile: 'calc(50% - (650px / 2))',
    tablet: 'calc(50% - (850px / 2))',
    md: 'calc(50% - (900px / 2))',
    mg: '0',
  },
};

const Requirements = () => {
  useGSAP(requirementsAnimation);
  const [swiper, setSwiper] = useState(null);

  return (
    <Box
      as="section"
      id={SECTIONS_IDS.requirements}
      w="full"
      h={{ mobile: '620px', tablet: 'clamp(620px, 108vw, 800px)', laptop: '905px' }}
      alignItems="start"
      pos="relative"
      overflow="hidden"
    >
      <BlockDiagonalDivider position="top" direction="left" zIndex="3" />

      <Box {...bgImagesStyles} pos="absolute" zIndex="1">
        <NextImage src={Bg} alt="Фоновое изображение" w="full" h="full" />
      </Box>

      <Box
        {...bgImagesStyles}
        id={`${SECTIONS_IDS.requirements}-cup`}
        pos="absolute"
        zIndex="2"
        opacity="0"
      >
        <NextImage src={Cup} alt="Кубок" w="full" h="full" />
      </Box>

      <Grid
        boxSize="full"
        templateColumns="100%"
        templateRows="1fr"
        justifyItems="center"
        pos="relative"
        zIndex="4"
      >
        <VStack
          id={`${SECTIONS_IDS.requirements}-first-container`}
          gridArea="1 / 1"
          w="full"
          gap={{ mobile: '32px', tablet: '40px', xxl: '65px' }}
          p={{ mobile: '123px 0 0', tablet: '95px 16px 0', laptop: '90px 80px 0' }}
          pos="relative"
          zIndex="5"
        >
          <Text
            id={`${SECTIONS_IDS.requirements}-title`}
            as="h2"
            fontSize={{ mobile: '32px', tablet: '36px', xxl: '60px' }}
            textTransform="uppercase"
            textAlign="center"
            color="ourWhite"
            opacity="0"
            pointerEvents="none"
          >
            Требования к кандидатам
          </Text>

          <Grid
            w="full"
            templateColumns={{ tablet: 'repeat(4, 1fr)', xxl: '1fr 322px 1fr' }}
            autoFlow={{ mobile: 'column', tablet: 'row' }}
            columnGap={{ mobile: '16px', tablet: '20px', xxl: '17px' }}
            rowGap={{ tablet: '20px', xxl: '45px' }}
            hideBelow="tablet"
          >
            {textCardsData.map((textCardData, i) => (
              <RequirementCard
                key={`${SECTIONS_IDS.requirements}-text-card-${i}`}
                id={`${SECTIONS_IDS.requirements}-text-card-${i}`}
                {...textCardData}
              />
            ))}
          </Grid>

          <Slider
            controller={swiper}
            setController={setSwiper}
            SlideComponent={RequirementCard}
            slidesData={textCardsData}
            containerProps={{
              id: `${SECTIONS_IDS.requirements}-cards-slider`,
              opacity: '0',
              pointerEvents: 'none',
              hideFrom: 'tablet',
              sx: {
                '.swiper-slide:first-of-type > *': { ml: '16px' },
                '.swiper-slide:last-of-type > *': { mr: '16px' },
              },
            }}
            slidesPerView="auto"
            spaceBetween="16px"
          />
        </VStack>

        <Flex
          id={`${SECTIONS_IDS.requirements}-second-container`}
          gridArea="1 / 1"
          boxSize="full"
          align="center"
          justify="center"
          px={{ mobile: '16px', tablet: '60px' }}
          opacity="0"
          pointerEvents="none"
          zIndex="6"
        >
          <VStack
            maxW={{ mobile: '430px', tablet: '605px', laptop: '922px' }}
            borderRadius={{ mobile: '24px', tablet: '32px', laptop: '48px' }}
            bgColor="ourWhite"
            gap={{ mobile: '14px', tablet: '24px', laptop: '32px' }}
            p={{ mobile: '18px', tablet: '40px', laptop: '48px' }}
          >
            <Text
              as="h3"
              textStyle="h2"
              fontSize={{ mobile: '24px', tablet: '38px', laptop: '60px' }}
              lineHeight="110%"
              letterSpacing="-1%"
              textAlign="center"
              textTransform="uppercase"
            >
              Готов вести команду вперёд?
            </Text>

            <ButtonGoTo p={{ mobile: '13px 40px', laptop: '20px 40px' }} />
          </VStack>
        </Flex>
      </Grid>

      <BlockShadow
        position="bottom"
        h={{ mobile: '62px', tablet: '90px', laptop: '155px' }}
        zIndex="3"
      />
    </Box>
  );
};

export default Requirements;

const RequirementCardComponent = ({ title, text, ...props }) => (
  <GridItem
    as={VStack}
    gridColumn={{ tablet: 'span 2', xxl: 'unset' }}
    w={{ mobile: 'fit-content', tablet: 'full', xxl: 'unset' }}
    h={{ mobile: '137px', xms: 'full', xxl: 'fit-content' }}
    alignItems="start"
    bgColor="ourWhite"
    borderRadius={{ mobile: '18px' }}
    p={{ mobile: '18px', tablet: '20px', laptop: '24px' }}
    gap="10px"
    opacity={{ tablet: '0' }}
    pointerEvents={{ tablet: 'none' }}
    {...props}
  >
    <Text as="h3" textStyle="b1" color="greenMuted">
      {title}
    </Text>

    <InterText textStyle="b3" color="ourGrayThree">
      {text}
    </InterText>
  </GridItem>
);

const RequirementCard = memo(RequirementCardComponent);
